package util

import kotlinx.browser.document
import org.khronos.webgl.get
import org.khronos.webgl.set
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement

object RenderHelper {
    /**
     * Replace the pixel data of the image so that the pixels that were very white are rendered as black
     * (hopefully the outlines) and the rest of the pixels that are not black/transparent are rendered as white
     * (hopefully the "bodies" of the objects).
     *
     * This is done by creating an invisible helper canvas and drawing the data url image to it, then
     * modifying the pixel data of the canvas to create the outline effect, and finally extracting the new data url.
     *
     * Note that we have to use `pixels.asDynamic()[...] = ...` to set the pixel data because the kotlin set
     * extension function erroneously takes a signed byte instead of an unsigned byte. Using that method we could
     * only set the pixel data to a value between 0 and 127.
     */
    fun outlineRender(dataUrl: String, callback: (String) -> Unit) {
        val image = document.createElement("img") as HTMLImageElement
        image.src = dataUrl

        image.onload = {
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            val ctx = canvas.getContext("2d") as CanvasRenderingContext2D

            canvas.width = image.width
            canvas.height = image.height
            ctx.drawImage(image, 0.0, 0.0)

            val imageData = ctx.getImageData(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
            val pixels = imageData.data

            for (i in 0 until pixels.length step 4) {
                // black outlines
                if (pixels[i] >= 127 && pixels[i + 1] >= 127 && pixels[i + 2] >= 127) {
                    pixels[i] = 0
                    pixels[i + 1] = 0
                    pixels[i + 2] = 0
                }
                // render the rest of the object in white
                else if (pixels[i] >= 2 && pixels[i + 1] >= 2 && pixels[i + 2] >= 2) {
                    pixels.asDynamic()[i] = 255
                    pixels.asDynamic()[i + 1] = 255
                    pixels.asDynamic()[i + 2] = 255
                }
                else {
                    pixels[i + 3] = 0
                }
            }

            ctx.putImageData(imageData, 0.0, 0.0)
            callback(canvas.toDataURL()) // Returns the new Data URL
        }
    }

}